import { Link2, StickyNote } from 'lucide-react'
import { useState } from 'react'

import { CallButton } from '@/components/Aircall/CallButton'
import { leadContactNumbers } from '@/components/Aircall/contactNumbers'
import { SmsButton } from '@/components/Aircall/SmsButton'
import { SmsComposer } from '@/components/Aircall/SmsComposer'
import { type AircallNumber } from '@/components/Aircall/types'
import { RichTextareaWithSubmit } from '@/components/composer/RichTextareaWithSubmit'
import { GoogleOauth } from '@/components/GoogleOauth'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { useEmailTemplates } from '@/models/emailTemplate'
import { type SalesLead, SalesLeadActivityType, useAddActivity } from '@/models/salesLead'
import { useCurrentUser } from '@/models/user'
import { ActivityFilter } from './ActivityFilter'
import { type ActivityGroup, inGroups } from './activityGroups'
import { ActivityItem } from './ActivityItem'
import { ActivityPages } from './ActivityPages'
import { AttachCallDialog } from './AttachCallDialog'
import { leadContactEmails, type LeadEmail } from './contactEmails'
import { EmailButton } from './EmailButton'
import { SendEmailForm } from './SendEmailForm'

interface ActivityTabProps {
  lead: SalesLead
}

export function ActivityTab({ lead }: ActivityTabProps) {
  const [newNote, setNewNote] = useState('')
  const [editorKey, setEditorKey] = useState(0)
  const [composerMode, setComposerMode] = useState<'idle' | 'note' | 'email' | 'sms'>('idle')
  const [smsRecipient, setSmsRecipient] = useState<AircallNumber | null>(null)
  const [emailRecipient, setEmailRecipient] = useState<LeadEmail | null>(null)
  const [composerKey, setComposerKey] = useState(0)
  const [replyToActivityId, setReplyToActivityId] = useState<string | null>(null)
  const [attachCallOpen, setAttachCallOpen] = useState(false)
  const [shownGroups, setShownGroups] = useState<ActivityGroup[]>([])

  const { user } = useCurrentUser({ enabled: true })
  const prefs = user?.hubPreferences
  const { data: emailTemplates = [] } = useEmailTemplates()
  const defaultEmailTemplateId = emailTemplates.find((t) => t.isDefault)?.id ?? null

  const addActivityMutation = useAddActivity()

  const primaryContact = lead.contacts.find((c) => c.isPrimary) ?? lead.contacts.at(0) ?? null
  const contactNumbers = leadContactNumbers(lead.contacts)
  const numbers = contactNumbers.length > 0 ? contactNumbers : undefined
  const emails = leadContactEmails(lead.contacts)

  return (
    <div className="flex flex-col gap-3">
      {composerMode === 'idle' && (
        <div className="flex flex-wrap gap-2">
          <GoogleOauth connectLabel="Connect Google to email">
            <EmailButton
              leadId={lead.id}
              emails={emails}
              onCompose={(recipient) => {
                setEmailRecipient(recipient)
                setComposerKey((k) => k + 1)
                setComposerMode('email')
              }}
            />
          </GoogleOauth>
          <Button
            variant="outline"
            className="border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100 hover:text-amber-800"
            onClick={() => setComposerMode('note')}
          >
            <StickyNote className="mr-1 size-4" />
            Note
          </Button>
          <CallButton leadId={lead.id} numbers={numbers} />
          <SmsButton
            numbers={numbers}
            leadId={lead.id}
            onCompose={(recipient) => {
              setSmsRecipient(recipient)
              setComposerMode('sms')
            }}
          />
          {numbers && (
            <Button variant="outline" onClick={() => setAttachCallOpen(true)}>
              <Link2 className="mr-1 size-4" />
              Link call
            </Button>
          )}
          {lead.activities.length > 0 && (
            <ActivityFilter
              className="ml-auto"
              activities={lead.activities}
              groups={shownGroups}
              onGroupsChange={setShownGroups}
            />
          )}
        </div>
      )}
      {composerMode === 'note' && (
        <RichTextareaWithSubmit
          editorKey={editorKey}
          value={newNote}
          onValueChange={setNewNote}
          placeholder="Add notes..."
          onSubmitMessage={({ message }) => {
            addActivityMutation.mutate(
              {
                id: lead.id,
                activityType: SalesLeadActivityType.NOTE,
                notes: message,
              },
              {
                onSuccess: () => {
                  setNewNote('')
                  setEditorKey((k) => k + 1)
                  setComposerMode('idle')
                },
              }
            )
          }}
          buttonProps={{ disabled: addActivityMutation.isPending }}
          addonChildren={
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setNewNote('')
                setComposerMode('idle')
              }}
            >
              Cancel
            </Button>
          }
        />
      )}
      {composerMode === 'email' && (
        <SendEmailForm
          lead={lead}
          templates={emailTemplates}
          defaultTemplateId={defaultEmailTemplateId}
          signature={prefs?.emailSignature || ''}
          recipientEmail={emailRecipient?.value ?? null}
          resetKey={composerKey}
          onSent={() => setComposerMode('idle')}
          onCancel={() => setComposerMode('idle')}
        />
      )}
      {composerMode === 'sms' && smsRecipient && (
        <SmsComposer
          leadId={lead.id}
          recipient={smsRecipient}
          contactName={primaryContact?.name ?? ''}
          onSent={() => {
            setComposerMode('idle')
            setSmsRecipient(null)
          }}
          onCancel={() => {
            setComposerMode('idle')
            setSmsRecipient(null)
          }}
        />
      )}

      {lead.activities.length === 0 ? (
        <Card>
          <CardContent>
            <p className="text-muted-foreground text-center">No activity yet</p>
          </CardContent>
        </Card>
      ) : (
        <ActivityPages
          // Keyed so another lead, or another filter, starts on its first page.
          key={`${lead.id}:${shownGroups.join()}`}
          activities={inGroups(lead.activities, shownGroups)}
          renderActivity={(activity) => (
            <ActivityItem
              lead={lead}
              activity={activity}
              // Carrier mail on the timeline must reply to the carrier, not the insured.
              carrier={lead.assignedCarriers.find((c) => c.id === activity.insuranceCarrier)}
              isReplying={replyToActivityId === activity.id}
              onReply={setReplyToActivityId}
              onCancelReply={() => setReplyToActivityId(null)}
            />
          )}
        />
      )}

      <AttachCallDialog leadId={lead.id} open={attachCallOpen} onClose={() => setAttachCallOpen(false)} />
    </div>
  )
}
